import { useState, useEffect } from "react";
import moment from "moment";
import { intl } from "LocaleWrapper";

import { PageHeader } from "@cofano/cck";
import { useLazyQuery } from "@apollo/client";
import { gql } from "__generated__/gql";
import { Grid2 as Grid, InputLabel, Alert } from "@mui/material";

import LocationSelect from "view/locations/components/selects/LocationSelect";
import WeekField from "components/form/WeekField";

import { DataFormat, Language } from "utils";

import "./styling/locationOccupation.scss";
import Helmet from "components/helmet/Helmet";
import { getTimeWindowsPerDay } from "view/locationplanning/utils";

const LocationOccupationOverview = () => {

	const [ data, setData ] = useState([]);
	const [ locations, setLocations ] = useState(localStorage.getItem("occupation.locations") ? JSON.parse(localStorage.getItem("occupation.locations"))  : []);
	const [ window, setWindow ] = useState({
		from: moment().startOf("isoWeek"),
		to: moment().endOf("isoWeek"),
	});

	const [ getLocationOccupations ] = useLazyQuery(QUERY, {
		onCompleted: (response) => setData(response.locationOccupations),
	});

	const buildHorizontalItems = () => {
		return Array.from(Array(7).keys()).map((e, index) => {
			const currentDate = moment(window.from).add(index, "day");
			return {
				name: moment(currentDate).format("ddd ll"),
				className: moment(currentDate).isSame(new Date(), "day") ? "isToday" : "",
				date: moment(currentDate),
			};
		});
	};

	const buildVerticalItems = () => {
		return locations.map((location) => ({
			name: location.description,
			location: location,
		}));
	};

	useEffect(() => {
		getLocationOccupations({
			variables: {
				timeWindows: getTimeWindowsPerDay(window.from, window.to),
				locationIds: locations.map(item => item.id),
			},
		});

		localStorage.setItem("occupation.locations", JSON.stringify(locations));
	}, [ locations, window ]);

	return (
		<div>
			<PageHeader>
				<PageHeader.Title>
					{Language("LOCATION_OCCUPATION")}
					<Helmet title={intl.formatMessage({ id: "LOCATION_OCCUPATION" })} />
				</PageHeader.Title>
			</PageHeader>

			<div className="location-occupation-overview">
				<div className="filter">
					<Grid container>
						<Grid size={{ xs: 12, sm: 4 }}>
							<InputLabel shrink>{Language("SELECT_LOCATIONS")}</InputLabel>
							<LocationSelect
								value={locations.length > 0 ? locations : null}
								onChange={options => setLocations(options || [])}
								multiple />
						</Grid>

						<Grid size={{ xs: 12, sm: 8 }}>
							<div className="week-filter">
								<WeekField
									defaultStart={window.from}
									onChange={(newWindow) => setWindow(newWindow)} />
							</div>
						</Grid>
					</Grid>
				</div>

				{locations.length > 0 ? (
					<Board
						horizontalItems={buildHorizontalItems()}
						verticalItems={buildVerticalItems()}
						renderItem={(verticalItem, horizontalItem) => {

							const { location } = verticalItem;
							const { date } = horizontalItem;

							const occupations = data.find(item => moment(item.window.from).isSame(moment(date), "day"))?.occupations || [];
							const occupation = occupations.find(item => item.location?.id === location?.id);

							return <DailyOverviewItem occupation={occupation} />;
						}} />
				) : (
					<Alert severity="warning">
						{Language("SELECT_LOCATION")}
					</Alert>
				)}
			</div>
		</div>
	);
};

const DailyOverviewItem = ({ occupation }) => {

	const amountOfShips = occupation?.ships || 0;
	const amountOfItems = occupation?.items || 0;
	const percentage = occupation?.percentage || 0;

	const getColor = () => {
		if (percentage >= 0 && percentage < 25) {
			return "green1";
		} else if (percentage >= 25 && percentage < 50) {
			return "green2";
		} else if (percentage >= 50 && percentage < 75) {
			return "yellow";
		} else if (percentage >= 75 && percentage < 100) {
			return "orange";
		} else {
			return "red";
		}
	};

	const color = getColor();

	return (
		<div className={`daily-overview-item ${color}`}>
			<div>
				<label>{DataFormat.tonnage(occupation?.tonnage || 0)}</label>
				<span>{amountOfShips} {intl.formatMessage({ id: amountOfShips === 1 ? "SHIP" : "SHIPS" }).toLowerCase()}</span>
			</div>

			<div>
				<label>{DataFormat.percentage(percentage, 0)}</label>
				<span>{amountOfItems} {intl.formatMessage({ id: amountOfItems === 1 ? "CALL" : "CALLS" }).toLowerCase()}</span>
			</div>
		</div>
	);
};

const Board = ({ horizontalItems, verticalItems, renderItem }) => {

	const itemWidth = 100 / (horizontalItems.length);

	return (
		<div className="board">
			<Sidebar
				verticalItems={verticalItems}
				itemWidth={itemWidth} />

			<div className="main">
				<Head
					horizontalItems={horizontalItems}
					itemWidth={itemWidth} />

				<Body
					horizontalItems={horizontalItems}
					verticalItems={verticalItems}
					itemWidth={itemWidth}
					renderItem={renderItem} />
			</div>
		</div>
	);
};

const Head = ({ horizontalItems, itemWidth }) => {
	return (
		<div className="head">
			{horizontalItems.map((item, index) => <div className={`cell ${item.className || ""}`} style={{ width: `${itemWidth}%` }} key={index}>{item.name}</div>)}
		</div>
	);
};

const Sidebar = ({ verticalItems }) => {
	return (
		<div className="sidebar">
			<div className="cell" />
			{verticalItems.map((item, index) => <div className="cell" key={index}>{item.name}</div>)}
		</div>
	);
};

const Body = ({ horizontalItems, verticalItems, itemWidth, renderItem }) => {
	return (
		<div className="body">
			{verticalItems.map((verticalItem, vIndex) => (
				<div className="row" key={vIndex}>
					{horizontalItems.map((horizontalItem, hIndex) => (
						<div className="cell" style={{ width: `${itemWidth}%` }} key={hIndex}>{renderItem(verticalItem, horizontalItem)}</div>
					))}
				</div>
			))}
		</div>
	);
};

const QUERY = gql(`#graphql
query locationOccupations($timeWindows: [TimeWindowInput!]!, $locationIds: [Long!]!) {
	locationOccupations(timeWindows: $timeWindows, locationIds: $locationIds) {
		window {
			from
			to
		}
		occupations {
			location {
				id
				description
			}
			tonnage
			ships
			items
			percentage
		}
	}
}
`);

export default LocationOccupationOverview;
